import React, { useEffect, useState } from 'react';
import { Button, Space, Tag, Popconfirm, message, Input, Card, Row, Col, Typography, Avatar, Tooltip, Badge } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, EyeOutlined, CalendarOutlined, UserOutlined, VideoCameraOutlined } from '@ant-design/icons';
import CustomPagination from '../components/CustomPagination';
import { blogService } from '../services/blogService';

const Blogs = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const load = async (page = 1, pageSize = 10, searchText = '') => {
    try {
      setLoading(true);
      const res = await blogService.list({ page, limit: pageSize, search: searchText });
      setData(res.items || []);
      setPagination({ current: res.page, pageSize: res.limit, total: res.total });
    } catch (err) {
      message.error('Erreur de chargement des blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onTableChange = (pag) => {
    load(pag.current, pag.pageSize, search);
  };

  const onDelete = async (id) => {
    try {
      await blogService.remove(id);
      message.success('Article supprimé');
      load(pagination.current, pagination.pageSize, search);
    } catch (err) {
      message.error('Suppression impossible');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="page blogs-page" style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Typography.Title level={2} style={{ margin: 0 }}>Articles de blog</Typography.Title>
          <Typography.Text type="secondary">Gérez vos articles et vidéos</Typography.Text>
        </div>
        <Button type="primary" size="large" onClick={() => navigate('/admin/blogs/new')}>
          Nouvel article
        </Button>
      </div>

      {/* Search */}
      <Card style={{ marginBottom: 24 }}>
        <Input.Search 
          placeholder="Rechercher par titre, contenu ou tags..." 
          allowClear 
          size="large"
          style={{ maxWidth: 400 }} 
          value={search} 
          onChange={(e) => setSearch(e.target.value)} 
          onSearch={() => load(1, pagination.pageSize, search)} 
        />
      </Card>

      {/* Articles Grid */}
      <Row gutter={[24, 24]}>
        {data.map(article => (
          <Col key={article.id} xs={24} sm={12} lg={8} xl={6}>
            <Card
              hoverable
              style={{ height: '100%', borderRadius: 12 }}
              bodyStyle={{ padding: 20 }}
              actions={[
                <Tooltip title="Modifier">
                  <Link to={`/admin/blogs/${article.id}/edit`}>
                    <EditOutlined key="edit" />
                  </Link>
                </Tooltip>,
                <Tooltip title="Supprimer">
                  <Popconfirm 
                    title="Supprimer cet article ?" 
                    onConfirm={() => onDelete(article.id)} 
                    okText="Oui" 
                    cancelText="Non"
                  >
                    <DeleteOutlined key="delete" style={{ color: '#ff4d4f' }} />
                  </Popconfirm>
                </Tooltip>
              ]}
            >
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <Badge 
                    status={article.isPublished ? 'success' : 'default'} 
                    text={article.isPublished ? 'Publié' : 'Brouillon'} 
                  />
                  {article.videoUrl && <VideoCameraOutlined style={{ color: '#1890ff' }} />}
                </div>
                
                <Typography.Title level={4} style={{ margin: 0, fontSize: 16, lineHeight: 1.4 }}>
                  {article.title}
                </Typography.Title>
                
                {article.excerpt && (
                  <Typography.Paragraph 
                    ellipsis={{ rows: 2 }} 
                    style={{ margin: '8px 0', color: '#666' }}
                  >
                    {article.excerpt}
                  </Typography.Paragraph>
                )}
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CalendarOutlined style={{ color: '#999' }} />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDate(article.publishedAt || article.createdAt)}
                    </Typography.Text>
                  </div>
                  <Typography.Text code style={{ fontSize: 11 }}>
                    {article.slug}
                  </Typography.Text>
                </div>
                
                {(() => {
                  const tags = Array.isArray(article.tags) ? article.tags : [];
                  if (!tags.length) return null;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {tags.slice(0, 3).map(tag => (
                        <Tag key={tag} size="small" color="blue">{tag}</Tag>
                      ))}
                      {tags.length > 3 && (
                        <Tag size="small" color="default">+{tags.length - 3}</Tag>
                      )}
                    </div>
                  );
                })()}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Pagination personnalisée */}
      {pagination.total > pagination.pageSize && (
        <CustomPagination
          current={pagination.current}
          total={pagination.total}
          pageSize={pagination.pageSize}
          onChange={(page, pageSize) => load(page, pageSize, search)}
          onShowSizeChange={(current, size) => load(1, size, search)}
          showSizeChanger={true}
          showQuickJumper={true}
          showTotal={true}
          size="default"
        />
      )}
    </div>
  );
};

export default Blogs;


